import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import Seo from '../components/Seo';

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      <Seo
        title="About"
        description="David Ball Management — a London actors' agency founded by David Ball. Meet the people, the philosophy and the story behind our small, deliberately curated client list."
        path="/about"
      />
      {/* Hero */}
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow>About David Ball Management</SectionEyebrow>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-10 items-end">
            <Reveal className="lg:col-span-8" delay={120}>
              <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                Careers built on <span className="italic font-light">relationships.</span>
              </h1>
            </Reveal>
            <Reveal className="lg:col-span-4" delay={240}>
              <p className="text-lg text-[#4a4a4a] leading-relaxed">
                David Ball Management is a London agency dedicated to representing actors with intelligence,
                warmth and a long horizon in mind.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <div className="img-hover rounded-2xl overflow-hidden aspect-[4/5]">
              <img
                src="/site-images/about-philosophy.jpg"
                alt="An agent at work"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:col-start-7" delay={180}>
            <SectionEyebrow>Our Philosophy</SectionEyebrow>
            <h2 className="font-heading text-4xl md:text-5xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
              Quality over quantity. Every time.
            </h2>
            <div className="mt-10 space-y-6 text-[#4a4a4a] leading-relaxed">
              <p>
                David Ball Management was founded to answer a simple question. What would an agency look like if it
                truly put the actor's career at the centre? Not the deal. Not the commission. The career.
              </p>
              <p>
                We work with a small client list because we believe in personal management.
                It is a relationship that requires attention, honesty and the kind of thinking that only
                comes from knowing an actor deeply.
              </p>
              <p>
                Our background is across casting, production and management. We know the rooms our clients
                walk into. We know the shape of the conversations before they happen. And we know that
                the most important work an agent does is often the least visible.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Team */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <Reveal>
          <SectionEyebrow>The People</SectionEyebrow>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase mt-6 leading-[0.95] text-[#002b5c]">
            The team.
          </h2>
        </Reveal>

        {/* David — photo left */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mt-16 items-start">
          <Reveal className="lg:col-span-5">
            <div className="img-hover rounded-2xl overflow-hidden aspect-[4/5] bg-neutral-100">
              <img
                src="https://customer-assets.emergentagent.com/job_casting-collective-1/artifacts/gbgwl0sk_dsc07395_.webp"
                alt="David Ball"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={150}>
            <p className="eyebrow">Agency Director</p>
            <h3 className="font-heading text-4xl md:text-5xl uppercase text-[#002b5c] mt-3">David Ball</h3>
            <p className="mt-8 text-[#4a4a4a] leading-relaxed">
              David believes great representation is built on trust, honesty and long-term thinking.
              He has been an agent for over 15 years, following work as an actor and
              later a producer. In 2008 he was the recipient of the Stage One / SOLT
              Apprentice Bursary and gained a coveted internship at both Sonia Friedman
              Productions and Act Productions Ltd. In 2010 he received the Stage One /
              SOLT Bursary for New Producers, and was subsequently made Associate
              Producer at Act Productions Ltd.
            </p>
            <p className="mt-4 text-sm text-[#4a4a4a] leading-relaxed">
              For David Ball Productions, credits include The Turn of the Screw (Almeida
              Theatre), Tommy in Concert (Prince Edward Theatre), The Sound of Music
              (Jordan, Beirut, Cairo), The Importance of Being Earnest (Jack Studio
              Theatre), Misterman (New Wimbledon Theatre / Jack Studio Theatre),
              Stepping Out (Union Theatre), The Clockmaker's Daughter in Concert
              (Cadogan Hall), West End &amp; Broadway (UK tour) and Miranda Sings Live in
              Leicester Square. Award nominations include two Off West End nominations
              for The Importance of Being Earnest and a West End Wilma nomination for
              The Clockmaker's Daughter in Concert.
            </p>
            <p className="mt-8 font-heading text-xl md:text-2xl italic text-[#002b5c]/80 leading-tight border-l-2 border-[#C5A059] pl-5">
              "The agent's work is invisible when it is done well. That is the point."
            </p>
          </Reveal>
        </div>

        {/* Divider */}
        <div className="mt-24 lg:mt-32 flex items-center gap-6 text-[11px] tracking-[0.24em] uppercase text-[#6b6b6b]">
          <span className="hairline flex-1 opacity-60" />
          <span>Alongside</span>
          <span className="hairline flex-1 opacity-60" />
        </div>

        {/* Evelyn — photo right */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mt-24 items-start">
          <Reveal className="lg:col-span-7 lg:order-1">
            <p className="eyebrow">Agent's Assistant</p>
            <h3 className="font-heading text-4xl md:text-5xl uppercase text-[#002b5c] mt-3">Evelyn Wong</h3>
            <p className="mt-8 text-[#4a4a4a] leading-relaxed">
              A graduate of Laine Theatre Arts, Evelyn has performed in the UK and
              internationally alongside her social media management, photography and
              teaching work. She is passionate about nurturing talent and is keen to
              expand her industry expertise behind the scenes with David Ball Management.
            </p>
            <div className="mt-8">
              <p className="eyebrow">Credits</p>
              <p className="mt-3 text-sm text-[#4a4a4a] leading-relaxed">
                Beetlejuice: The Musical (Norwegian Viva) &middot; K-Pop Fever
                (International Tour) &middot; Snow White (Wyvern Theatre) &middot;
                Regent Seven Seas Mariner &middot; Better Man.
              </p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:order-2" delay={150}>
            <div className="img-hover rounded-2xl overflow-hidden aspect-[4/5] bg-neutral-100">
              <img
                src="/client-photos/evelyn-wong.webp"
                alt="Evelyn Wong"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#002b5c] text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24 lg:py-32 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
            Curious <span className="italic font-light text-[#C5A059]">to talk?</span>
          </h2>
          <Link to="/contact" className="inline-flex items-center gap-3 rounded-full bg-[#C5A059] text-[#001a38] px-9 py-4 text-[11px] tracking-[0.24em] uppercase hover:bg-white transition-all duration-500">
            Get in touch <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
